import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { PageHeader } from "@/components/PageHeader";
import { Plus, X, FileText } from "lucide-react";
import { ORDER_CATEGORY_LABEL, ORDER_STATUS_LABEL, fmtDate } from "@/lib/format";
import { Field } from "./Fleet";

const DEFAULT_CHECKLIST = {
  maintenance: [
    "Revisión de aceite y filtros",
    "Inspección de frenos",
    "Revisión de neumáticos",
    "Estado de batería",
    "Niveles de líquidos (refrigerante, freno, dirección)",
    "Luces y sistema eléctrico",
    "Suspensión y dirección",
  ],
  repair: ["Diagnóstico inicial", "Reparación realizada", "Prueba final"],
  service: ["Servicio realizado", "Verificación de funcionamiento"],
  others: ["Item 1"],
};

export default function Workshop() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get("/service-orders").then((r) => setOrders(r.data));
  useEffect(() => {
    load();
    api.get("/vehicles").then((r) => setVehicles(r.data));
  }, []);

  const canCreate = user.role === "workshop_admin" || user.role === "superadmin";

  return (
    <div data-testid="workshop-page">
      <PageHeader
        tag="Taller"
        title="Órdenes de Servicio"
        description="Crea órdenes de mantención, reparación o servicio. Cierra órdenes con checklist firmado."
        action={
          canCreate && (
            <button onClick={() => setShowForm(true)} className="h-touch px-6 bg-black text-white text-sm font-bold uppercase tracking-wider hover:bg-blue-600 flex items-center gap-2" data-testid="new-order-btn">
              <Plus size={18} strokeWidth={2.5} /> Nueva Orden
            </button>
          )
        }
      />

      <div className="border border-gray-200 overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <Th>Patente</Th>
              <Th>Categoría</Th>
              <Th>Descripción</Th>
              <Th>Fecha</Th>
              <Th>Estado</Th>
              <Th></Th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id} className="border-b border-gray-200 hover:bg-gray-50" data-testid={`order-row-${o.id}`}>
                <td className="px-4 py-4 font-mono font-bold">{o.vehicle?.plate || "-"}</td>
                <td className="px-4 py-4 text-sm">{ORDER_CATEGORY_LABEL[o.category]}</td>
                <td className="px-4 py-4 text-sm truncate max-w-xs">{o.description}</td>
                <td className="px-4 py-4 text-sm">{fmtDate(o.created_at)}</td>
                <td className="px-4 py-4 text-xs font-bold uppercase tracking-wider">{ORDER_STATUS_LABEL[o.status]}</td>
                <td className="px-4 py-4 text-right">
                  <Link to={`/taller/orden/${o.id}`} className="text-xs font-bold uppercase tracking-wider text-blue-600 underline" data-testid={`open-order-${o.id}`}>
                    Abrir
                  </Link>
                </td>
              </tr>
            ))}
            {orders.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-sm text-gray-500">Sin órdenes registradas.</td></tr>}
          </tbody>
        </table>
      </div>

      {showForm && (
        <NewOrderModal vehicles={vehicles} onClose={() => setShowForm(false)} onDone={() => { setShowForm(false); load(); }} />
      )}
    </div>
  );
}

function Th({ children }) {
  return <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-700">{children}</th>;
}

function NewOrderModal({ vehicles, onClose, onDone }) {
  const [form, setForm] = useState({
    vehicle_id: "",
    category: "maintenance",
    description: "",
    mileage: 0,
    notes: "",
  });
  const [checklist, setChecklist] = useState(DEFAULT_CHECKLIST.maintenance.map((label) => ({ label, checked: false })));
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => {
    const v = e.target.value;
    setForm({ ...form, [k]: v });
    if (k === "category") {
      setChecklist((DEFAULT_CHECKLIST[v] || []).map((label) => ({ label, checked: false })));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await api.post("/service-orders", {
        ...form,
        mileage: parseInt(form.mileage, 10) || 0,
        checklist,
      });
      onDone();
    } catch (e) {
      setError(e.response?.data?.detail || "Error");
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center md:p-6">
      <div className="bg-white border-4 border-black w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h2 className="font-display text-2xl">Nueva Orden de Servicio</h2>
          <button onClick={onClose}><X size={24} strokeWidth={2.5} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-5" data-testid="new-order-form">
          <div>
            <label className="veta-tag block mb-2">Vehículo</label>
            <select required value={form.vehicle_id} onChange={update("vehicle_id")} className="w-full h-touch px-3 border border-gray-300" data-testid="order-vehicle-select">
              <option value="">Seleccione...</option>
              {vehicles.map((v) => <option key={v.id} value={v.id}>{v.plate} · {v.brand} {v.model}</option>)}
            </select>
            {vehicles.length === 0 && <p className="text-xs text-gray-500 mt-1">No tienes vehículos asignados al taller. Pide al Rent-a-Car que asigne uno.</p>}
          </div>
          <div>
            <label className="veta-tag block mb-2">Categoría</label>
            <select value={form.category} onChange={update("category")} className="w-full h-touch px-3 border border-gray-300" data-testid="order-category-select">
              <option value="maintenance">Mantención</option>
              <option value="repair">Reparación</option>
              <option value="service">Servicio</option>
              <option value="others">Otros</option>
            </select>
          </div>
          <div>
            <label className="veta-tag block mb-2">Descripción</label>
            <textarea required value={form.description} onChange={update("description")} rows={3} className="w-full px-3 py-3 border border-gray-300" data-testid="order-description" />
          </div>
          <Field label="Kilometraje" type="number" value={form.mileage} onChange={update("mileage")} testId="order-mileage" />

          <div>
            <p className="veta-tag mb-3">Checklist técnico</p>
            <div className="border border-gray-200 divide-y divide-gray-200">
              {checklist.map((item, i) => (
                <div key={i} className="p-3 flex items-center gap-3">
                  <input type="checkbox" checked={item.checked} onChange={(e) => setChecklist(checklist.map((x, idx) => idx === i ? { ...x, checked: e.target.checked } : x))} className="w-5 h-5" />
                  <input value={item.label} onChange={(e) => setChecklist(checklist.map((x, idx) => idx === i ? { ...x, label: e.target.value } : x))} className="flex-1 h-10 px-2 border border-gray-200 text-sm" />
                </div>
              ))}
            </div>
            <button type="button" onClick={() => setChecklist([...checklist, { label: "", checked: false }])} className="mt-2 text-xs font-bold uppercase tracking-wider text-blue-600 underline">+ Agregar item</button>
          </div>

          {error && <div className="status-danger px-4 py-3 text-sm">{error}</div>}
          <button type="submit" disabled={loading} className="w-full h-touch bg-black text-white text-sm font-bold uppercase tracking-wider hover:bg-blue-600 disabled:opacity-60" data-testid="save-order-btn">
            {loading ? "Guardando..." : "Crear Orden"}
          </button>
        </form>
      </div>
    </div>
  );
}
